import i18n from "i18next";
import { useState } from "react";
import { IconCheck, IconInfoCircle } from "@tabler/icons-react";
import type {
  CaptionPosition, DocumentLook, LookPaper, LookTables, TableDensity, TableStyle, ThemeDescriptor, ThemeId,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { Badge } from "@/components/lilia-ui/badge";
import { NEW_FEATURES, useNewBadgeOnOpen } from "@/lib/newBadge";
import {
  chapterColours, classReason, isSelectable, normaliseTables, resolveTables, themeAllowed, unavailableReason, validPin, type Chapter,
} from "@/lib/themes/look";
import { testIds } from "@/testIds";
import { ThemeThumbnail } from "./ThemeThumbnail";
import { TableStyleSample } from "./TableStyleSample";

interface LookSectionProps {
  value: DocumentLook;
  onChange: (look: DocumentLook) => void;
  themes: readonly ThemeDescriptor[];
  themesLoading?: boolean;
  themesError?: boolean;
  /** Why themes are off (a publisher class), or null. */
  lookLocked?: string | null;
  /** The themes the document's class may use (beamer: Classic and Exposition); null for all. */
  lookThemes?: readonly ThemeId[] | null;
  /** The document's chapters (numbered top-level headings), for pins. */
  chapters: readonly Chapter[];
}

const PAPER_OPTIONS: { value: LookPaper; label: string }[] = [
  { value: "theme", label: i18n.t("editor:look.themeSPaper") },
  { value: "white", label: i18n.t("editor:look.whiteAlways") },
];

const STYLE_OPTIONS: { value: TableStyle; label: string }[] = [
  { value: "ruled", label: i18n.t("editor:look.ruled") },
  { value: "banded", label: i18n.t("editor:look.banded") },
  { value: "header", label: i18n.t("editor:look.headerBand") },
];
const DENSITY_OPTIONS: { value: TableDensity; label: string }[] = [
  { value: "normal", label: i18n.t("editor:look.normal") },
  { value: "compact", label: i18n.t("editor:look.compact") },
];
const CAPTION_OPTIONS: { value: CaptionPosition; label: string }[] = [
  { value: "above", label: i18n.t("editor:look.above") },
  { value: "below", label: i18n.t("editor:look.below") },
];

/** A row of mutually exclusive options, in the tonal selected state. */
function Segmented<T extends string>({
  label,
  testId,
  optionTestId,
  value,
  options,
  onPick,
}: {
  label: string;
  testId: string;
  optionTestId: string;
  value: T;
  options: { value: T; label: string }[];
  onPick: (v: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-testid={testId}
      data-value={value}
      className="justify-self-start inline-flex flex-wrap gap-1 p-0.5 rounded-[7px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]"
    >
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            data-testid={optionTestId}
            data-value={o.value}
            onClick={() => onPick(o.value)}
            className={cn(
              "h-6 px-2.5 rounded-[5px] border text-[12px] cursor-pointer transition-colors duration-150",
              on ? TONAL : "border-transparent bg-transparent text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** The tonal selected state (tables-modern turn 3): never grey. */
const TONAL =
  "bg-[var(--lilia-primary-subtle)] text-[color:var(--lilia-primary-ink)] border-[color-mix(in_srgb,var(--lilia-primary)_32%,transparent)] font-semibold";

/**
 * Document settings → Look (design-handoffs/2026-10-05-document-themes, 1e).
 * Controlled: the dialog owns the value and saves it with the other settings.
 */
export function LookSection({
  value,
  onChange,
  themes,
  themesLoading = false,
  themesError = false,
  lookLocked = null,
  lookThemes = null,
  chapters,
}: LookSectionProps) {
  const [pinsOpen, setPinsOpen] = useState(false);
  // The Tables row is new (phase 2): a New badge for 30 days, gone for this
  // person once they've opened Look (warm accent §2).
  const tablesNew = useNewBadgeOnOpen(NEW_FEATURES.lookTables);

  const locked = !!lookLocked;
  // Only the themes this document's class may use (the user, 10 Oct: Exposition
  // greyed "Beamer only" on every report read as broken; Olivia agreed). Planned
  // themes stay, greyed: they're coming. A locked class keeps its Classic card.
  const shown = locked
    ? themes.filter((t) => t.id === "classic")
    : themes.filter((t) => themeAllowed(t.id, lookThemes));
  const selectedId: ThemeId = locked || !themeAllowed(value.theme, lookThemes) ? "classic" : value.theme;
  const selected = themes.find((t) => t.id === selectedId);
  const sequence = !locked && selected?.sequence?.length ? selected.sequence : null;

  const pick = (theme: ThemeDescriptor) => {
    if (locked || !isSelectable(theme) || !themeAllowed(theme.id, lookThemes)) return;
    onChange({ ...value, theme: theme.id });
  };

  const setPin = (chapterId: string, raw: string) => {
    const pins = { ...value.pins };
    if (raw === "") delete pins[chapterId];
    else pins[chapterId] = Number(raw);
    onChange({ ...value, pins });
  };

  const colours = sequence ? chapterColours(chapters, sequence, value.pins) : null;

  // A beamer deck (its class may use a class-specific theme, Exposition) has no table styles:
  // the server ignores look.tables there, so the Tables rows are not offered.
  const isDeck = !!lookThemes && themes.some((t) => !!t.classes?.length && lookThemes.includes(t.id));

  // Look → Tables (2f). Under a publisher class only Ruled, and the class
  // owns density and captions, so those rows are not offered.
  const tables = resolveTables(value.tables, selected, locked);
  const setTables = (patch: LookTables) => {
    if (locked) return;
    onChange({ ...value, tables: { ...normaliseTables(value.tables), ...patch } });
  };

  return (
    <div data-testid={testIds.look.section} className="flex flex-col gap-3">
      {themesLoading && themes.length === 0 && (
        <p className="text-xs text-[var(--lilia-text-muted)]">{i18n.t("editor:look.loadingLooks")}</p>
      )}
      {themesError && themes.length === 0 && (
        <p className="text-xs text-[var(--lilia-text-muted)]">{"The looks couldn't load. The document keeps its current one."}</p>
      )}

      {shown.length > 0 && (
        <div
          role="radiogroup"
          aria-label={i18n.t("editor:look.documentTheme")}
          className={cn("grid gap-2", locked ? "grid-cols-6" : "grid-cols-3 sm:grid-cols-6")}
        >
          {shown.map((t) => {
            const isSelected = t.id === selectedId;
            const reason = classReason(t, lookThemes) ?? unavailableReason(t);
            const disabled = !!reason || locked;
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={disabled && !isSelected ? true : undefined}
                disabled={!!reason}
                title={reason ? `${t.name}: ${reason.toLowerCase()}` : t.name}
                data-testid={testIds.look.theme(t.id)}
                data-selected={isSelected ? "true" : "false"}
                onClick={() => pick(t)}
                className={cn(
                  "flex flex-col gap-1.5 p-0 border-0 bg-transparent text-left",
                  reason ? "cursor-not-allowed" : locked ? "cursor-default" : "cursor-pointer group",
                )}
              >
                <div
                  className={cn(
                    "relative rounded-[7px] transition-shadow duration-150",
                    isSelected
                      ? "border-2 border-[var(--lilia-primary)] shadow-[0_0_0_3px_var(--lilia-primary-subtle)]"
                      : "border border-[var(--lilia-border-strong)] group-hover:border-[var(--lilia-primary)]",
                    reason && "opacity-45",
                  )}
                >
                  <ThemeThumbnail theme={t} className="h-[88px]" />
                  {/* Never colour alone (REPLY-2026-10-06d §1): the border, this
                      check and the 600 name. Decorative: aria-checked says it. */}
                  {isSelected && (
                    <span
                      data-testid={testIds.look.selectedBadge}
                      aria-hidden="true"
                      className="absolute top-1 right-1 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[var(--lilia-primary)] text-white"
                    >
                      <IconCheck size={12} stroke={3} />
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-[11.5px] text-center leading-tight",
                    isSelected ? "font-semibold text-[color:var(--lilia-primary-ink)]" : "font-normal text-[var(--lilia-text)]",
                    reason && "text-[var(--lilia-text-muted)]",
                  )}
                >
                  {t.name}
                </span>
                {reason && (
                  <span className="-mt-1 text-[10.5px] text-center text-[var(--lilia-text-muted)]">{reason}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selected && !locked && (
        <p data-testid={testIds.look.forLine} className="m-0 text-[12.5px] leading-snug text-[var(--lilia-text-muted)]">
          <b className="text-[var(--lilia-text)]">{selected.name}</b> · {i18n.t(`editor:look.for.${selected.id}`, { defaultValue: selected.for })}
        </p>
      )}

      {locked && (
        <div
          data-testid={testIds.look.lockedNote}
          className="flex gap-2 items-start px-3 py-2.5 rounded-lg bg-[var(--lilia-bg-secondary)] text-[12.5px] leading-snug text-[var(--lilia-text)]"
        >
          <IconInfoCircle size={15} className="mt-[1px] shrink-0 text-[var(--lilia-text-muted)]" aria-hidden="true" />
          <span>{lookLocked}</span>
        </div>
      )}

      {!locked && (
        <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2.5 items-center text-[12.5px]">
          <span className="text-xs font-semibold text-[var(--lilia-text)]">{i18n.t("editor:look.paper")}</span>
          <div
            role="radiogroup"
            aria-label={i18n.t("editor:look.paper")}
            data-testid={testIds.look.paper}
            data-value={value.paper}
            className="justify-self-start inline-flex gap-1 p-0.5 rounded-[7px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]"
          >
            {PAPER_OPTIONS.map((o) => {
              const on = value.paper === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  data-testid={testIds.look.paperOption}
                  data-value={o.value}
                  onClick={() => onChange({ ...value, paper: o.value })}
                  className={cn(
                    "h-6 px-2.5 rounded-[5px] border text-[12px] cursor-pointer transition-colors duration-150",
                    on ? TONAL : "border-transparent bg-transparent text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]",
                  )}
                >
                  {o.label}
                </button>
              );
            })}
          </div>

          {sequence && (
            <>
              <span className="text-xs font-semibold text-[var(--lilia-text)] self-start pt-1">{i18n.t("editor:look.sectionColours")}</span>
              <div className="flex flex-col gap-2 min-w-0">
                <div className="flex flex-wrap items-center gap-1" data-testid={testIds.look.sequence} aria-label="Index sequence">
                  {sequence.map((c, i) => (
                    <span
                      key={i}
                      data-colour={c}
                      title={`Colour ${i + 1}`}
                      className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-[5px] text-[10.5px] font-bold text-white"
                      style={{ background: c }}
                    >
                      {i + 1}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid={testIds.look.pinOpen}
                  aria-expanded={pinsOpen}
                  onClick={() => setPinsOpen((o) => !o)}
                  className="self-start p-0 border-0 bg-transparent text-[12.5px] font-semibold text-[color:var(--lilia-primary-ink)] cursor-pointer underline-offset-2 hover:underline"
                >
                  {i18n.t("editor:look.pinAColourToA")}
                </button>
                {pinsOpen && (
                  <PinList chapters={chapters} sequence={sequence} pins={value.pins} colours={colours} onPin={setPin} />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {shown.length > 0 && !isDeck && (
        <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2.5 items-center text-[12.5px]">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--lilia-text)]">
            {i18n.t("editor:look.tables")}
            {tablesNew && <Badge variant="new" />}
          </span>
          <Segmented
            label={i18n.t("editor:look.tableStyle")}
            testId={testIds.look.tablesStyle}
            optionTestId={testIds.look.tablesStyleOption}
            value={tables.style}
            options={locked ? STYLE_OPTIONS.filter((o) => o.value === "ruled") : STYLE_OPTIONS}
            onPick={(style) => setTables({ style })}
          />
          {!locked && (
            <>
              <span className="text-xs font-semibold text-[var(--lilia-text)]">{i18n.t("editor:look.density")}</span>
              <Segmented
                label={i18n.t("editor:look.tableDensity")}
                testId={testIds.look.tablesDensity}
                optionTestId={testIds.look.tablesDensityOption}
                value={tables.density}
                options={DENSITY_OPTIONS}
                onPick={(density) => setTables({ density })}
              />
              <span className="text-xs font-semibold text-[var(--lilia-text)]">{i18n.t("editor:look.captions")}</span>
              <Segmented
                label={i18n.t("editor:look.captionPosition")}
                testId={testIds.look.captionPosition}
                optionTestId={testIds.look.captionPositionOption}
                value={tables.caption}
                options={CAPTION_OPTIONS}
                onPick={(caption) => setTables({ caption })}
              />
            </>
          )}
          {selected && (
            <>
              <span />
              <TableStyleSample theme={selected} tables={tables} paper={locked ? "theme" : value.paper} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PinList({
  chapters,
  sequence,
  pins,
  colours,
  onPin,
}: {
  chapters: readonly Chapter[];
  sequence: readonly string[];
  pins: Readonly<Record<string, number>>;
  colours: ReturnType<typeof chapterColours> | null;
  onPin: (chapterId: string, raw: string) => void;
}) {
  if (chapters.length === 0) {
    return (
      <p className="m-0 text-[12px] text-[var(--lilia-text-muted)]">
        {i18n.t("editor:look.noNumberedTopLevelHeadings")}
      </p>
    );
  }
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-1 max-h-[220px] overflow-y-auto thin-scrollbar pr-1">
      {chapters.map((c) => {
        const cc = colours?.get(c.id);
        const pin = validPin(pins[c.id], sequence);
        return (
          <li key={c.id} className="flex items-center gap-2 min-w-0">
            <span
              className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded-[5px] text-[10.5px] font-bold text-white shrink-0"
              style={{ background: cc?.colour }}
            >
              {c.label}
            </span>
            <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--lilia-text)]" title={c.title}>
              {c.title}
            </span>
            <select
              data-testid={testIds.look.pinColour}
              data-block-id={c.id}
              aria-label={`Colour for ${c.title}`}
              value={pin === null ? "" : String(pin)}
              onChange={(e) => onPin(c.id, e.target.value)}
              className="h-7 shrink-0 rounded-md border border-[var(--lilia-border-strong)] bg-[var(--lilia-bg)] text-[var(--lilia-text)] text-[12px] px-1.5 focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary)]"
            >
              <option value="">{pin === null && cc ? `In order (${cc.sequenceIndex + 1})` : i18n.t("editor:look.inOrder")}</option>
              {sequence.map((colour, i) => (
                <option key={i} value={String(i)} style={{ color: colour }}>
                  {`■ Colour ${i + 1}`}
                </option>
              ))}
            </select>
          </li>
        );
      })}
    </ul>
  );
}
